/**
 * VideoViewerWithMarkers - Video player with timeline markers for detected tells
 * Features: Auto-pause at tell timestamps, feedback modal for validation
 * Converted from TSX to JSX for college_recruiter_v1
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Maximize2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, X, SkipBack, SkipForward, RefreshCw, Send, Loader2 } from 'lucide-react';
import { getConfidenceLevel, getConfidenceColor, PITCH_TYPE_LABELS } from '../../lib/wireTypes';
import { TellValidationButtons } from './TellValidationButtons';
import { MatrixBackground } from '../v2/analyzer/MatrixBackground';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Convert GCS URI to public HTTPS URL
 * @param {string} gcsUri
 * @returns {string}
 */
function gcsUriToPublicUrl(gcsUri) {
  if (gcsUri.startsWith('gs://')) {
    return gcsUri.replace('gs://', 'https://storage.googleapis.com/');
  }
  return gcsUri;
}

/**
 * Parse timestamp string (MM:SS or M:SS) to seconds
 * @param {string} timestampStr - e.g. "00:06" or "0:06"
 * @returns {number} - seconds
 */
function parseTimestampToSeconds(timestampStr) {
  if (!timestampStr || typeof timestampStr !== 'string') return null;
  const match = timestampStr.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const minutes = parseInt(match[1], 10);
  const seconds = parseInt(match[2], 10);
  return minutes * 60 + seconds;
}

/**
 * Parse timestamps from indicator description text (fallback)
 * Matches patterns like "0:10", "00:03", "0:04-0:05", etc.
 * @param {string} text
 * @returns {number[]}
 */
function parseTimestampsFromText(text) {
  const timestamps = [];
  const regex = /(\d{1,2}):(\d{2})/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    timestamps.push(minutes * 60 + seconds);
  }
  return [...new Set(timestamps)].sort((a, b) => a - b);
}

/**
 * Format seconds to MM:SS
 * @param {number} seconds
 * @returns {string}
 */
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// =============================================================================
// TIMELINE MARKER COMPONENT
// =============================================================================

/**
 * @param {Object} props
 * @param {Object} props.marker
 * @param {number} props.position - Percentage position (0-100)
 * @param {boolean} props.isActive
 * @param {Function} props.onClick
 */
function TimelineMarker({ marker, position, isActive, onClick }) {
  const level = getConfidenceLevel(marker.confidence);
  const color = getConfidenceColor(level);

  return (
    <button
      onClick={onClick}
      className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-all duration-200
        ${isActive ? 'scale-150 ring-2 ring-white' : 'hover:scale-125'}`}
      style={{
        left: `${position}%`,
        backgroundColor: color,
        transform: `translate(-50%, -50%) ${isActive ? 'scale(1.5)' : ''}`,
      }}
      title={`${marker.tell} @ ${formatTime(marker.timestamp)}`}
    />
  );
}

// =============================================================================
// TELL OVERLAY WITH FEEDBACK MODAL
// =============================================================================

/**
 * Tell Overlay with Feedback Modal - Shows when video pauses at tell timestamp
 * @param {Object} props
 * @param {Object|null} props.marker - The active marker/tell
 * @param {Function} [props.onDismiss] - Dismiss callback
 * @param {Function} [props.onFeedbackSubmitted] - Called after feedback, resumes video
 * @param {string} [props.pitcher] - Pitcher name for feedback
 * @param {string} [props.videoUri] - Video URI for feedback
 * @param {boolean} [props.showFeedback=true] - Whether to show feedback buttons
 */
function TellOverlay({
  marker,
  onDismiss,
  onFeedbackSubmitted,
  pitcher,
  videoUri,
  showFeedback = true
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    setIsExpanded(true);
    setFeedbackGiven(false);
  }, [marker]);

  if (!marker) return null;

  const level = getConfidenceLevel(marker.confidence);
  const color = getConfidenceColor(level);
  const pitchType = marker.pitchRevealed || marker.pitchTypesAffected?.[0] || 'UNK';

  const handleFeedback = (type) => {
    setFeedbackGiven(true);
    // Collapse after short delay and resume video
    setTimeout(() => {
      setIsExpanded(false);
      onFeedbackSubmitted?.();
    }, 500);
  };

  return (
    <div
      className="absolute top-2 left-2 right-2 z-30 animate-fadeIn"
      onClick={(e) => e.stopPropagation()}
    >
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/20 hover:border-white/30 transition-colors"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(16px) saturate(180%)',
            WebkitBackdropFilter: 'blur(16px) saturate(180%)'
          }}
        >
          <div
            className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse"
            style={{ backgroundColor: color }}
          />
          <span className="text-white text-sm font-medium truncate max-w-[200px]">
            {marker.category || marker.tell}
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded font-mono flex-shrink-0"
            style={{ backgroundColor: `${color}30`, color }}
          >
            {level}
          </span>
          <ChevronDown className="w-4 h-4 text-white/60 flex-shrink-0" />
        </button>
      ) : (
        <div
          className="rounded-xl border shadow-2xl overflow-hidden"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderColor: `${color}60`,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 0 1px ${color}20`
          }}
        >
          {/* Header with pitch type and confidence */}
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{
              backgroundColor: `${color}25`,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <span className="font-black text-white text-lg block drop-shadow-sm">
                  {marker.tell || marker.category}
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-white/30 text-white text-xs font-bold font-mono rounded">
                    {pitchType}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded font-bold font-mono"
                    style={{ backgroundColor: `${color}50`, color }}
                  >
                    {(marker.confidence * 100).toFixed(0)}% confidence
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white font-bold font-mono bg-black/40 px-2 py-1 rounded">
                @ {formatTime(marker.timestamp)}
              </span>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 rounded hover:bg-white/10 transition-colors"
                aria-label="Minimize"
              >
                <ChevronUp className="w-4 h-4 text-white/60" />
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="px-4 py-4 bg-black/20">
            <p className="text-sm text-white font-semibold leading-relaxed">
              {marker.description}
            </p>
          </div>

          {/* Feedback Section */}
          {showFeedback && !feedbackGiven && (
            <div className="px-4 py-4 border-t border-white/10 bg-black/25">
              <p className="text-sm text-white font-semibold mb-3">Is this tell accurate?</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleFeedback('accurate')}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600/40 text-green-300 rounded-lg hover:bg-green-600/60 transition-colors border-2 border-green-500/60"
                >
                  <ThumbsUp className="w-5 h-5" />
                  <span className="text-base font-bold">Accurate</span>
                </button>
                <button
                  onClick={() => handleFeedback('inaccurate')}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600/40 text-red-300 rounded-lg hover:bg-red-600/60 transition-colors border-2 border-red-500/60"
                >
                  <ThumbsDown className="w-5 h-5" />
                  <span className="text-base font-bold">Not Accurate</span>
                </button>
                <button
                  onClick={() => {
                    setIsExpanded(false);
                    onFeedbackSubmitted?.();
                  }}
                  className="px-3 py-3 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-white/70 mt-3 font-medium">
                Marking as "Not Accurate" will exclude this pattern from future analysis
              </p>
            </div>
          )}

          {/* Feedback Confirmation */}
          {feedbackGiven && (
            <div className="px-4 py-3 border-t border-white/20 bg-green-600/30 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span className="text-base text-green-300 font-bold">Feedback recorded</span>
            </div>
          )}

          {/* Footer */}
          {!showFeedback && (
            <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-xs text-white/40">
                Click marker on timeline to view other tells
              </span>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="text-xs text-white/50 hover:text-white/80 transition-colors"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// REANALYZE MODAL COMPONENT
// =============================================================================

/**
 * Reanalyze Modal - Allows user to provide instructions for re-analysis
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onSubmit - Called with user instructions
 * @param {boolean} props.isLoading
 * @param {Object} props.analysis - Current video analysis for context
 */
function ReanalyzeModal({ isOpen, onClose, onSubmit, isLoading, analysis }) {
  const [instructions, setInstructions] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(instructions);
  };

  // Show matrix rain behind everything when loading
  if (isLoading) {
    return (
      <>
        {/* Matrix rain covers everything behind the modal */}
        <MatrixBackground isActive={true} sportCategory="baseball" />
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-zinc-900/95 border border-amber-500/30 rounded-xl w-full max-w-lg shadow-2xl shadow-amber-500/10">
            {/* Header */}
            <div className="px-4 py-3 border-b border-amber-500/20 flex items-center gap-3">
              <div className="relative">
                <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                <div className="absolute inset-0 w-6 h-6 rounded-full bg-amber-400/20 animate-ping" />
              </div>
              <div>
                <h3 className="font-bold text-amber-400">ECHO Reanalyzing...</h3>
                <p className="text-xs text-zinc-400">Scanning biomechanical gates</p>
              </div>
            </div>
            {/* Loading content */}
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                {['GLOVE', 'HEAD', 'SHOULDERS', 'ARM', 'LOWER HALF', 'RHYTHM', 'RELEASE'].map((gate, i) => (
                  <div key={gate} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${i < 4 ? 'bg-amber-400 animate-pulse' : 'bg-zinc-600'}`} />
                    <span className={`text-sm font-mono ${i < 4 ? 'text-amber-400' : 'text-zinc-500'}`}>
                      Gate {i + 1}: {gate}
                    </span>
                    {i < 3 && <span className="text-xs text-green-400">✓</span>}
                    {i === 3 && <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />}
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-500 text-center mt-4">
                Analyzing with Gemini 2.5 Flash...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Extract existing tells for context display
  const existingTells = analysis?.indicators?.map(i => i.tell || i.category).filter(Boolean) || [];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            <h3 className="font-bold text-white">Reanalyze Video</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current analysis context */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="text-xs text-zinc-400 mb-2">Current Analysis:</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-zinc-700 rounded text-sm font-mono text-white">
                {analysis?.pitchTypeGroundTruth || analysis?.pitchType || 'Unknown Pitch'}
              </span>
              {analysis?.setPosition && (
                <span className={`px-2 py-0.5 rounded text-sm font-medium ${
                  analysis.setPosition === 'Stretch'
                    ? 'bg-purple-500/80 text-white'
                    : 'bg-blue-500/80 text-white'
                }`}>
                  {analysis.setPosition}
                </span>
              )}
              <span className="text-xs text-zinc-500">{analysis?.gameDate}</span>
            </div>
            {existingTells.length > 0 && (
              <div className="text-xs text-zinc-400">
                <span className="text-zinc-500">Detected tells:</span>{' '}
                {existingTells.slice(0, 3).join(', ')}
                {existingTells.length > 3 && ` +${existingTells.length - 3} more`}
              </div>
            )}
          </div>

          {/* Instructions input */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              What should the AI look for?
            </label>
            <textarea
              ref={textareaRef}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Watch the glove position more closely, I think he's showing the grip before the set position..."
              className="w-full h-24 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <p className="text-xs text-zinc-500 mt-1">
              This will replace the current analysis with new findings. Press ⌘+Enter to submit.
            </p>
          </div>

          {/* Model info */}
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Using Gemini 2.5 Flash for enhanced detection
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-zinc-700 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Reanalyze
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN VIDEO VIEWER COMPONENT
// =============================================================================

/**
 * Video Viewer with Timeline Markers
 * @param {Object} props
 * @param {Object} props.analysis - VideoAnalysis object
 * @param {Function} [props.onClose]
 * @param {Function} [props.onNavigatePrev] - Navigate to previous video
 * @param {Function} [props.onNavigateNext] - Navigate to next video
 * @param {Function} [props.onApprove] - Called when user approves video (green thumbs up)
 * @param {Function} [props.onReject] - Called when user rejects video (red thumbs down)
 * @param {Function} [props.onReanalyze] - Called with user instructions for re-analysis
 * @param {Function} [props.onAnalysisUpdated] - Called when analysis is updated after reanalyze
 * @param {number} [props.currentIndex] - Current video index (1-based for display)
 * @param {number} [props.totalCount] - Total number of videos
 * @param {string} [props.className]
 */
export default function VideoViewerWithMarkers({
  analysis,
  onClose,
  onNavigatePrev,
  onNavigateNext,
  onApprove,
  onReject,
  onReanalyze,
  onAnalysisUpdated,
  currentIndex,
  totalCount,
  className = ''
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeMarker, setActiveMarker] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reanalyze modal state
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  // Video feedback state
  const [feedbackAnimation, setFeedbackAnimation] = useState(null); // 'approve' | 'reject' | null

  const videoUrl = useMemo(() => gcsUriToPublicUrl(analysis.gcsUri), [analysis.gcsUri]);

  // Track which markers have been visited (to avoid re-pausing on same marker)
  const visitedMarkersRef = useRef(new Set());
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(true);

  const markers = useMemo(() => {
    const allMarkers = [];
    const indicators = analysis.indicators || [];

    for (const indicator of indicators) {
      const tell = indicator.tell;
      const category = indicator.category || indicator.categoryLabel;
      const confidence = indicator.confidence;
      const description = indicator.description || indicator.whatToLookFor;
      const pitchRevealed = indicator.pitchRevealed || indicator.pitchTypesAffected?.[0];

      // PRIORITY 1: Use dedicated timestamp field if available
      let timestamp = null;
      if (indicator.timestamp) {
        timestamp = parseTimestampToSeconds(indicator.timestamp);
      }

      // PRIORITY 2: Fall back to parsing from description
      if (timestamp === null && description) {
        const parsed = parseTimestampsFromText(description);
        if (parsed.length > 0) {
          timestamp = parsed[0];
        }
      }

      // Only add marker if we have a valid timestamp
      if (timestamp !== null && timestamp >= 0) {
        allMarkers.push({
          timestamp,
          tell,
          category,
          confidence,
          description,
          pitchRevealed,
          videoUid: indicator.videoUid,
        });
      }
    }

    return allMarkers.sort((a, b) => a.timestamp - b.timestamp);
  }, [analysis.indicators]);

  // Auto-pause at marker timestamps
  useEffect(() => {
    if (!isPlaying || !autoPauseEnabled || markers.length === 0) return;

    // Find marker within 0.2 seconds of current time that hasn't been visited
    const threshold = 0.2;
    const markerToTrigger = markers.find(m => {
      const timeDiff = Math.abs(m.timestamp - currentTime);
      const markerKey = `${m.timestamp}-${m.tell}`;
      return timeDiff < threshold && !visitedMarkersRef.current.has(markerKey);
    });

    if (markerToTrigger) {
      const markerKey = `${markerToTrigger.timestamp}-${markerToTrigger.tell}`;
      visitedMarkersRef.current.add(markerKey);

      // AUTO-PAUSE at the exact moment
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
      setActiveMarker(markerToTrigger);
    }
  }, [currentTime, markers, isPlaying, autoPauseEnabled]);

  // CRITICAL: Reset ALL state when video/analysis changes
  // This fixes the bug where old tell overlays persisted when switching videos
  useEffect(() => {
    // Reset visited markers
    visitedMarkersRef.current.clear();
    // Reset active marker (the tell overlay)
    setActiveMarker(null);
    // Reset playback state
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    setError(null);
    // Reset video element
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, [analysis.videoUid, analysis.gcsUri]);

  // Reset markers when indicators change (e.g., after reanalysis)
  const indicatorsKey = useMemo(() =>
    JSON.stringify(analysis.indicators?.map(i => i.tell || i.category) || []),
    [analysis.indicators]
  );

  useEffect(() => {
    // Clear visited markers so auto-pause works on new tells
    visitedMarkersRef.current.clear();
    setActiveMarker(null);
    console.log('[VideoViewer] Indicators updated, markers reset');
  }, [indicatorsKey]);

  // Handle marker proximity without auto-pause (for highlighting in timeline)
  useEffect(() => {
    if (!isPlaying) return;

    const current = markers.find(m =>
      Math.abs(m.timestamp - currentTime) < 1
    );

    // Only update active marker display, don't pause
    if (current && current !== activeMarker && !autoPauseEnabled) {
      setActiveMarker(current);
    } else if (!current && activeMarker && Math.abs(activeMarker.timestamp - currentTime) > 2) {
      // Clear active marker when we've moved past it
      if (!autoPauseEnabled) {
        setActiveMarker(null);
      }
    }
  }, [currentTime, markers, activeMarker, isPlaying, autoPauseEnabled]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleError = useCallback(() => {
    setError('Failed to load video. Check CORS configuration.');
    setIsLoading(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const restart = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = 0;
    setCurrentTime(0);
    setActiveMarker(null);
  }, []);

  const seekTo = useCallback((time) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleMarkerClick = useCallback((marker) => {
    seekTo(marker.timestamp);
    setActiveMarker(marker);
  }, [seekTo]);

  const handleProgressClick = useCallback((e) => {
    if (!videoRef.current || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    seekTo(newTime);
  }, [duration, seekTo]);

  const skipFrame = useCallback((direction) => {
    if (!videoRef.current) return;
    const skipAmount = direction === 'next' ? 0.1 : -0.1;
    const newTime = Math.max(0, Math.min(duration, currentTime + skipAmount));
    seekTo(newTime);
  }, [currentTime, duration, seekTo]);

  // Resume playback after feedback is submitted
  const handleFeedbackSubmitted = useCallback(() => {
    // Resume playback after a short delay
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }, 300);
  }, []);

  // Handle video approval (green thumbs up) - dismisses and advances
  const handleApprove = useCallback(() => {
    setFeedbackAnimation('approve');
    // Trigger callback after animation starts
    setTimeout(() => {
      onApprove?.(analysis);
    }, 300);
  }, [analysis, onApprove]);

  // Handle video rejection (red thumbs down)
  const handleReject = useCallback(() => {
    setFeedbackAnimation('reject');
    setTimeout(() => {
      onReject?.(analysis);
      setFeedbackAnimation(null);
    }, 500);
  }, [analysis, onReject]);

  // Handle reanalyze submission
  const handleReanalyzeSubmit = useCallback(async (instructions) => {
    setIsReanalyzing(true);
    try {
      const result = await onReanalyze?.(analysis, instructions);
      setShowReanalyzeModal(false);
      if (result && onAnalysisUpdated) {
        onAnalysisUpdated(result);
      }
    } catch (err) {
      console.error('Reanalyze failed:', err);
    } finally {
      setIsReanalyzing(false);
    }
  }, [analysis, onReanalyze, onAnalysisUpdated]);

  const toggleFullscreen = useCallback(() => {
    if (!videoRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      videoRef.current.requestFullscreen();
    }
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Video Header - Compact with Navigation */}
      <div className="px-3 py-2 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between">
        {/* Left: Prev button + Pitch info */}
        <div className="flex items-center gap-2">
          {onNavigatePrev && (
            <button
              onClick={onNavigatePrev}
              disabled={currentIndex <= 1}
              className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous video"
            >
              <SkipBack className="w-4 h-4 text-white" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono font-bold text-white">
              {analysis.pitchTypeGroundTruth || analysis.pitchType || 'UNK'}
            </span>
            {/* Set position badge (Windup/Stretch) */}
            {analysis.setPosition && (
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                analysis.setPosition === 'Stretch'
                  ? 'bg-purple-500/80 text-white'
                  : 'bg-blue-500/80 text-white'
              }`}>
                {analysis.setPosition}
              </span>
            )}
            <span className="text-xs text-white/40">
              {analysis.gameDate}
            </span>
          </div>
        </div>

        {/* Center: Video count */}
        {totalCount && (
          <span className="text-xs text-white/50 font-mono">
            {currentIndex} / {totalCount}
          </span>
        )}

        {/* Right: Tells badge + Next button + Close */}
        <div className="flex items-center gap-2">
          {analysis.tippingDetected && markers.length > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded font-mono"
              style={{
                backgroundColor: `${getConfidenceColor(getConfidenceLevel(analysis.confidence))}20`,
                color: getConfidenceColor(getConfidenceLevel(analysis.confidence))
              }}
            >
              {markers.length} tell{markers.length !== 1 ? 's' : ''}
            </span>
          )}
          {onNavigateNext && (
            <button
              onClick={onNavigateNext}
              disabled={currentIndex >= totalCount}
              className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next video"
            >
              <SkipForward className="w-4 h-4 text-white" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          )}
        </div>
      </div>

      {/* Video Container */}
      <div className="relative aspect-video bg-black">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-4">
              <p className="text-red-400 text-sm">{error}</p>
              <p className="text-white/50 text-xs mt-2 font-mono break-all max-w-md">
                {videoUrl}
              </p>
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          muted={isMuted}
          playsInline
          crossOrigin="anonymous"
        />

        <div
          className="absolute inset-x-0 top-0 z-20 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TellOverlay
            marker={activeMarker}
            onFeedbackSubmitted={handleFeedbackSubmitted}
            pitcher={analysis.pitcher}
            videoUri={analysis.gcsUri}
            showFeedback={autoPauseEnabled}
          />
        </div>

        <button
          onClick={togglePlay}
          className="absolute inset-0 cursor-pointer focus:outline-none"
          style={{ zIndex: 1 }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        />
      </div>

      {/* Timeline with Markers */}
      <div className="px-4 py-2 bg-zinc-900">
        <div
          className="relative h-6 cursor-pointer group"
          onClick={handleProgressClick}
        >
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-zinc-700 rounded-full -translate-y-1/2 group-hover:h-1.5 transition-all">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>

          {duration > 0 && markers.map((marker, i) => (
            <TimelineMarker
              key={i}
              marker={marker}
              position={(marker.timestamp / duration) * 100}
              isActive={activeMarker === marker}
              onClick={() => handleMarkerClick(marker)}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-1 text-xs text-white/50 font-mono">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 bg-zinc-900 border-t border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-white" />
            ) : (
              <Play className="w-4 h-4 text-white" />
            )}
          </button>

          <button
            onClick={restart}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label="Restart"
          >
            <RotateCcw className="w-4 h-4 text-white" />
          </button>

          <button
            onClick={toggleMute}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4 text-white/60" />
            ) : (
              <Volume2 className="w-4 h-4 text-white" />
            )}
          </button>

          {/* Auto-pause at tells toggle */}
          {markers.length > 0 && (
            <button
              onClick={() => {
                setAutoPauseEnabled(!autoPauseEnabled);
                // Reset visited markers if re-enabling
                if (!autoPauseEnabled) {
                  visitedMarkersRef.current.clear();
                }
              }}
              className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                autoPauseEnabled
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
              title={autoPauseEnabled ? 'Auto-pause at tells (enabled)' : 'Auto-pause at tells (disabled)'}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {autoPauseEnabled ? 'Auto-Pause ON' : 'Auto-Pause OFF'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => skipFrame('prev')}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label="Previous frame"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>

          <button
            onClick={() => skipFrame('next')}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label="Next frame"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
            aria-label="Fullscreen"
          >
            <Maximize2 className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Video-Level Feedback Buttons */}
      {(onApprove || onReanalyze || onReject) && (
        <div className="px-4 py-3 bg-zinc-900 border-t border-zinc-800">
          <div className="flex items-center justify-center gap-3">
            {/* Green Thumbs Up - Approve */}
            {onApprove && (
              <button
                onClick={handleApprove}
                disabled={feedbackAnimation === 'approve'}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                  feedbackAnimation === 'approve'
                    ? 'bg-green-500 text-white scale-110'
                    : 'bg-green-600/30 text-green-400 hover:bg-green-600/50 border border-green-500/40 hover:border-green-500/60'
                }`}
                title="Approve analysis - advance to next video"
              >
                <ThumbsUp className="w-5 h-5" />
                <span>Approve</span>
              </button>
            )}

            {/* Reanalyze Button - In the middle */}
            {onReanalyze && (
              <button
                onClick={() => setShowReanalyzeModal(true)}
                disabled={isReanalyzing}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm bg-blue-600/30 text-blue-400 hover:bg-blue-600/50 border border-blue-500/40 hover:border-blue-500/60 transition-all disabled:opacity-50"
                title="Reanalyze video with custom instructions"
              >
                {isReanalyzing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
                <span>{isReanalyzing ? 'Analyzing...' : 'Reanalyze'}</span>
              </button>
            )}

            {/* Red Thumbs Down - Reject */}
            {onReject && (
              <button
                onClick={handleReject}
                disabled={feedbackAnimation === 'reject'}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                  feedbackAnimation === 'reject'
                    ? 'bg-red-500 text-white scale-110'
                    : 'bg-red-600/30 text-red-400 hover:bg-red-600/50 border border-red-500/40 hover:border-red-500/60'
                }`}
                title="Mark as inaccurate"
              >
                <ThumbsDown className="w-5 h-5" />
                <span>Reject</span>
              </button>
            )}
          </div>
          <p className="text-xs text-zinc-500 text-center mt-2">
            Approve to confirm analysis & advance • Reanalyze with custom instructions
          </p>
        </div>
      )}

      {/* Marker Legend */}
      {markers.length > 0 && (
        <div className="px-4 py-3 bg-zinc-950 border-t border-zinc-800">
          <div className="text-xs text-white/50 mb-2">Detected Tells:</div>
          <div className="flex flex-wrap gap-2">
            {markers.map((marker, i) => {
              const level = getConfidenceLevel(marker.confidence);
              const color = getConfidenceColor(level);
              return (
                <button
                  key={i}
                  onClick={() => handleMarkerClick(marker)}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    activeMarker === marker
                      ? 'ring-1 ring-white/50'
                      : 'hover:bg-zinc-800'
                  }`}
                  style={{
                    backgroundColor: `${color}20`,
                    color,
                  }}
                >
                  {formatTime(marker.timestamp)} - {marker.category ? `${marker.category}` : marker.tell}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Feedback Animation Overlay */}
      {feedbackAnimation && (
        <div className={`absolute inset-0 z-40 flex items-center justify-center transition-all duration-300 ${
          feedbackAnimation === 'approve'
            ? 'bg-green-500/30 animate-pulse'
            : 'bg-red-500/30 animate-pulse'
        }`}>
          <div className={`p-6 rounded-full ${
            feedbackAnimation === 'approve' ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {feedbackAnimation === 'approve' ? (
              <ThumbsUp className="w-12 h-12 text-white" />
            ) : (
              <ThumbsDown className="w-12 h-12 text-white" />
            )}
          </div>
        </div>
      )}

      {/* Reanalyze Modal */}
      <ReanalyzeModal
        isOpen={showReanalyzeModal}
        onClose={() => setShowReanalyzeModal(false)}
        onSubmit={handleReanalyzeSubmit}
        isLoading={isReanalyzing}
        analysis={analysis}
      />
    </div>
  );
}
